import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

export type TFilterPreviewState = 'idle' | 'loading' | 'error';
interface IProps {
  onSubmit?: () => void;
  state: TFilterPreviewState;
  total: number | null;
  isDisabled?: boolean;
  isCampgroundsSearchPage?: boolean;
}

const SearchPreviewResults: React.FC<IProps> = ({
  onSubmit,
  state,
  total,
  isDisabled,
  isCampgroundsSearchPage,
}) => {
  const intl = useIntl();
  const noResultsMessage = isCampgroundsSearchPage
    ? intl.formatMessage({
        defaultMessage: 'No Campgrounds available',
        id: 'RITAbD',
        description: 'Search > Filters: label with no results - Campgrounds',
      })
    : intl.formatMessage({
        defaultMessage: 'No RVs available',
        id: 'C0kBBm',
        description: 'Search > Filters: label with no results',
      });

  const resultsMessage = isCampgroundsSearchPage
    ? intl.formatMessage(
        {
          defaultMessage: 'Show {total, plural, one {# Campground} other {# Campgrounds}}',
          id: 'caV5ZL',
          description: 'Search > Filters: label with preview results - Campgrounds',
        },
        { total },
      )
    : intl.formatMessage(
        {
          defaultMessage: 'Show {total, plural, one {# RV} other {# RVs}}',
          id: 'JZeWmw',
          description: 'Search > Filters: label with preview results',
        },
        { total },
      );

  const defaultMessage = isCampgroundsSearchPage
    ? intl.formatMessage({
        defaultMessage: 'Show Campgrounds',
        id: 'x1jIhV',
        description: 'Search > Filters: default label - Campgrounds',
      })
    : intl.formatMessage({
        defaultMessage: 'Show RVs',
        id: 'iYo5qy',
        description: 'Search > Filters: default label',
      });

  const handleSubmit = useCallback(() => {
    if (state === 'loading') return;
    onSubmit?.();
  }, [state, onSubmit]);

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    // Prevent click event from bubbling up to elements underneath the modal
    event.stopPropagation();
  }, []);

  // Determine button label based on state and total
  const getButtonLabel = () => {
    if (state === 'error') {
      // Count is unknown (the preview fetch failed) — don't claim 0 results
      return defaultMessage;
    }
    if (isDisabled) {
      return defaultMessage;
    }
    if (total !== null) {
      return total > 0 ? resultsMessage : noResultsMessage;
    }
    return defaultMessage;
  };

  // A 0-result count or a failed preview fetch shouldn't block the user from
  // executing the search they've already configured — only the external
  // isDisabled prop should.
  const isButtonDisabled = isDisabled;

  return (
    <Button
      className="md:w-auto"
      onClick={handleSubmit}
      onClickCapture={handleClickCapture}
      label={getButtonLabel()}
      variant={EButtonColorVariant.Primary}
      loading={state === 'loading'}
      disabled={isButtonDisabled}
    />
  );
};

export default SearchPreviewResults;
